import React, { useRef } from 'react';
import { ImageIcon, Upload, X } from 'lucide-react';
import { useUserImages, useUploadImage } from '../../../services/userImageService';

const BrandImagePicker = ({
  label,
  value,
  onChange,
  hint,
  aspectClass = 'aspect-video',
  uploadImageType = 'general',
  showGallery = true,
  galleryParams = { non_portfolio: true },
  /** Optional curated list (e.g. business logo only). Used instead of the full gallery when provided. */
  presetImages = null,
  galleryTitle = 'Choose from your gallery',
  /**
   * When set, shown when `value` is empty (e.g. business logo default).
   * Selecting the fallback clears the override (onChange(null)) instead of re-saving it.
   */
  fallbackUrl = null,
}) => {
  const fileInputRef = useRef(null);
  const usePresets = Array.isArray(presetImages);
  const { data: imagesResponse, isLoading } = useUserImages(
    showGallery && !usePresets ? galleryParams : {},
    { staleTime: 60_000, enabled: showGallery && !usePresets }
  );
  const uploadMutation = useUploadImage();

  const images = usePresets
    ? presetImages.filter((img) => img?.image_url)
    : showGallery
      ? (imagesResponse?.data || imagesResponse?.images || []).filter(
          (img) =>
            img.image_type !== 'portfolio' &&
            img.image_type !== 'document' &&
            img.image_type !== 'logo'
        )
      : [];

  const displayUrl = value || fallbackUrl || null;
  const hasOverride = Boolean(value);

  const handleUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);
    formData.append('image_type', uploadImageType);

    uploadMutation.mutate(
      { formData },
      {
        onSuccess: (res) => {
          const url = res?.data?.image_url || res?.image_url;
          if (url) onChange(url);
        },
      }
    );
    event.target.value = '';
  };

  const selectUrl = (url) => {
    if (!url || (fallbackUrl && url === fallbackUrl)) {
      onChange(null);
      return;
    }
    onChange(url);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <label className="text-sm font-medium text-gray-900">{label}</label>
        {hasOverride && (
          <button
            type="button"
            onClick={() => onChange(null)}
            className="text-xs text-gray-500 hover:text-red-600 inline-flex items-center gap-1"
          >
            <X className="w-3 h-3" />
            Use business logo
          </button>
        )}
      </div>

      {displayUrl ? (
        <div className={`relative overflow-hidden rounded-lg border border-gray-200 bg-surface-muted ${aspectClass}`}>
          <img src={displayUrl} alt={label} className="w-full h-full object-cover" />
          {!hasOverride && fallbackUrl ? (
            <span className="absolute bottom-1.5 left-1.5 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white">
              Business logo
            </span>
          ) : null}
        </div>
      ) : (
        <div className={`flex items-center justify-center rounded-lg border-2 border-dashed border-gray-200 bg-surface-muted ${aspectClass}`}>
          <div className="text-center px-4 py-6">
            <ImageIcon className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No image selected</p>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploadMutation.isPending}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-surface-muted disabled:opacity-50"
        >
          <Upload className="w-4 h-4" />
          {uploadMutation.isPending ? 'Uploading…' : 'Upload new'}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleUpload}
        />
      </div>

      {hint && <p className="text-xs text-gray-500">{hint}</p>}

      {(usePresets || showGallery) && (images.length > 0 || (!usePresets && isLoading)) && (
        <div>
          <p className="text-xs font-medium text-gray-600 mb-2">{galleryTitle}</p>
          <div className="grid grid-cols-4 sm:grid-cols-5 gap-2 max-h-36 overflow-y-auto pr-1">
            {!usePresets && isLoading ? (
              [...Array(4)].map((_, i) => (
                <div key={i} className="aspect-square rounded-md bg-gray-100 animate-pulse" />
              ))
            ) : (
              images.map((img, idx) => {
                const url = img.image_url;
                if (!url) return null;
                const isFallback = Boolean(fallbackUrl && url === fallbackUrl);
                const selected = isFallback ? !hasOverride : value === url;
                return (
                  <button
                    key={img.id || url || idx}
                    type="button"
                    onClick={() => selectUrl(url)}
                    title={img.label || undefined}
                    className={`relative aspect-square rounded-md overflow-hidden border-2 transition-all ${
                      selected
                        ? 'border-black ring-2 ring-black/20'
                        : 'border-transparent hover:border-gray-300'
                    }`}
                  >
                    <img
                      src={url}
                      alt={img.label || ''}
                      className="w-full h-full object-contain bg-surface"
                    />
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BrandImagePicker;
